import {
  Typography,
  Checkbox,
  TextInput,
  Button,
  JoonUIColor,
  Modal,
  ModalHeader,
} from "@joonapp/web-shared"
import { useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import Counter from "../../components/counter/Counter"
import { SelectRow } from "../../components/selectRow/SelectRow"
import { SelectRowContainer } from "../../components/selectRowContainer/SelectRowContainer"
import { QUERY_KEYS } from "../../constants"
import { sendBonusReward } from "../../networking/bonusRewards"
import { useFamilyQuery } from "../../networking/queries"
import { useTodaysBonusRewardsQuery } from "../../networking/rewards/queries"
import { FamilyQuerySelect, RewardsTodayType } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { createErrorFromResponse } from "../../util/util"

interface DeductCoinsModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void

  selectedChildUserIds: number[]
  selectChild: (id: number) => void
  coinAmount: number
  setCoinAmount: (amount: number) => void
  rewardDescription: string
  setRewardDescription: (description: string) => void
  isSendingReward: boolean
  setIsSendingReward: (isSending: boolean) => void
  error: string | null
  setError: (error: string | null) => void
}

const initialState = {
  isOpen: false,
  selectedChildUserIds: [],
  coinAmount: 12,
  rewardDescription: "",
  isSendingReward: false,
  error: null,
}

export const useDeductCoinsModalStore = create<DeductCoinsModalStore>(
  (set) => ({
    ...initialState,
    onOpen: () => {
      trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_DEDUCT_COINS)
      set({ isOpen: true })
    },
    onClose: () => set(initialState),
    selectChild: (id: number) => {
      set((state) => ({
        selectedChildUserIds: state.selectedChildUserIds.includes(id)
          ? state.selectedChildUserIds.filter((childId) => childId !== id)
          : [...state.selectedChildUserIds, id],
      }))
    },
    setCoinAmount: (amount) => set({ coinAmount: amount }),
    setRewardDescription: (description) =>
      set({ rewardDescription: description }),
    setIsSendingReward: (isSending) => set({ isSendingReward: isSending }),
    setError: (error) => set({ error }),
  })
)

const DeductCoinsModal = () => {
  const {
    isOpen,
    onClose: closeBonusRewardModal,
    selectedChildUserIds,
    selectChild,
    coinAmount,
    setCoinAmount,
    rewardDescription,
    setRewardDescription,
    isSendingReward,
    setIsSendingReward,
    error,
    setError,
  } = useDeductCoinsModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const childUsers = children?.map((child) => child.user)
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const { data: childrenWhoReceivedDeductions } = useTodaysBonusRewardsQuery(
    RewardsTodayType.DEDUCTION
  )

  const childrenWithDeductionsAvailable =
    childUsers?.filter(
      (child) => !childrenWhoReceivedDeductions?.includes(child.id)
    ) || []

  const sendReward = async () => {
    if (selectedChildUserIds.length === 0) {
      throw setError(t("DCM_ERR_SELECT_CHILD"))
    } else if (rewardDescription.length === 0) {
      throw setError(t("DCM_ERR_DESCRIPTION"))
    }
    setError(null)

    setIsSendingReward(true)

    try {
      const rewardPromises = selectedChildUserIds.map((childId) =>
        sendBonusReward(childId, -coinAmount, rewardDescription)
      )
      await Promise.all(rewardPromises)
      trackAnalyticEvent(ANALYTIC_EVENTS.SEND_COIN_DEDUCTIONS)
      closeBonusRewardModal()
      queryClient.invalidateQueries([QUERY_KEYS.BONUS_REWARDS])
    } catch (errorResponse) {
      const errorResponseString = createErrorFromResponse(errorResponse)
      if (errorResponseString) setError(errorResponseString)
      else setError(t("REWARD_ERR_GENERIC"))
    }
    setIsSendingReward(false)
  }

  const minCoinsInSelectedChildren = useMemo(() => {
    let min = Infinity
    const selectedChildren = children?.filter((child) =>
      selectedChildUserIds.includes(child.user.id)
    )
    selectedChildren?.forEach((child) => {
      if (child.user.coins < min) min = child.user.coins
    })
    return min
  }, [selectedChildUserIds, children])

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeBonusRewardModal}
      animate
      mobileFull
      style={{ width: "min(500px, 100%)" }}
    >
      <ModalHeader
        title={t("REWARD_DEDUCT_COINS")}
        onClose={closeBonusRewardModal}
        showBorderBottom
      />
      <div className="flex flex-col p-6 gap-4 pt-4 overflow-y-auto">
        <div className="flex flex-col gap-1">
          <Typography variant="body">{t("DCM_DESC")}</Typography>
          <Typography variant="bodySmall">{t("DCM_ONE_PER_DAY")}</Typography>
        </div>

        <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-3">
          <Typography variant="bodySmallBold">{t("DCM_WHO_FROM")}</Typography>
          {childrenWithDeductionsAvailable?.length > 0 ? (
            <SelectRowContainer>
              {childrenWithDeductionsAvailable.map((child, i) => (
                <SelectRow
                  key={i}
                  onClick={() => selectChild(child?.id)}
                  hideBorder
                  showBorderBottom={
                    i !== childrenWithDeductionsAvailable.length - 1
                  }
                >
                  <div className="flex flex-col">
                    <Typography variant="bodySmallBold" className="text-left">
                      {child?.name}
                    </Typography>
                    <Typography
                      variant="bodySmall"
                      color={JoonUIColor.text.secondary}
                    >
                      {t("DCM_CURRENT_BALANCE", { coins: child?.coins })}
                    </Typography>
                  </div>
                  <Checkbox
                    name="test"
                    onChange={() => {}}
                    selected={selectedChildUserIds.includes(child.id)}
                    hideBorder
                  />
                </SelectRow>
              ))}
            </SelectRowContainer>
          ) : (
            <Typography variant="body">{t("DCM_ALL_SENT")}</Typography>
          )}
        </div>
        <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-3">
          <Typography variant="bodySmallBold">
            {t("DCM_SELECT_AMOUNT")}
          </Typography>
          <Counter
            value={coinAmount}
            setValue={setCoinAmount}
            maxValue={minCoinsInSelectedChildren}
          />
          <Typography variant="bodySmall">
            {minCoinsInSelectedChildren !== Infinity
              ? t("DCM_MAX_DEDUCT_AMOUNT", {
                  count: minCoinsInSelectedChildren,
                })
              : t("DCM_MAX_DEDUCT")}
          </Typography>
        </div>
        <div className="flex flex-col gap-1">
          <Typography variant="bodySmallBold">{t("DCM_SHARE_WHY")}</Typography>
          <TextInput
            name="rewardDescription"
            value={rewardDescription}
            onChange={(e: any) => setRewardDescription(e.target.value)}
            placeholder={t("DCM_PLACEHOLDER")}
            fullWidth
            maxLength={100}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 bg-white px-6 py-4 border-t border-borderPrimary">
        {error && (
          <Typography
            variant="bodySmallBold"
            style={{ textAlign: "center", width: "100%" }}
            color={JoonUIColor.semantic.alert}
          >
            {error}
          </Typography>
        )}
        <Button
          text={t("REWARD_DEDUCT_COINS")}
          isLoading={isSendingReward}
          onClick={sendReward}
          fullWidth
          disabled={
            isSendingReward ||
            selectedChildUserIds.length === 0 ||
            coinAmount === 0 ||
            rewardDescription.length === 0
          }
        />
      </div>
    </Modal>
  )
}

export default DeductCoinsModal
